import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Plus, Trash2, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError, showConfirm } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { creditNotesService } from '@/services/creditNotesService'
import { financeService } from '@/services/financeService'
import {
  CreditNote,
  CreditNoteLine,
  FinanceCustomer,
  Item,
  TaxRate,
  CreditNoteListItem,
} from '@/api/types'

export const CreditNotesDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const creditNoteFromList = (location.state as any)?.creditNote as CreditNoteListItem | undefined
  const branchId = user?.selectedBranch?.branchId
  const companyId = 0 // Placeholder until company context is available

  // Helper function to find 15% VAT tax rate (prioritize taxRateId: 1)
  const getDefaultTaxRate = (taxRates: TaxRate[]): { taxRateId: number; taxRate: number } | null => {
    // First, try to find taxRateId === 1
    const taxById = taxRates.find(t => t.taxRateId === 1)
    if (taxById) {
      return {
        taxRateId: taxById.taxRateId,
        taxRate: taxById.taxPercent || taxById.taxRate || 0
      }
    }
    // Fallback: find by 15% rate
    const tax15 = taxRates.find(t => {
      const rate = t.taxPercent || t.taxRate || 0
      return Math.abs(rate - 15) < 0.01 // Allow small floating point differences
    })
    if (tax15) {
      return {
        taxRateId: tax15.taxRateId,
        taxRate: tax15.taxPercent || tax15.taxRate || 0
      }
    }
    return null
  }

  const getDefaultRows = (taxRates: TaxRate[] = []): CreditNoteLine[] => {
    const defaultTax = getDefaultTaxRate(taxRates)
    return Array.from({ length: 3 }, (): CreditNoteLine => ({
      lineId: 0,
      creditNoteId: 0,
      itemId: 0,
      description: '',
      qty: 0,
      unitPrice: 0,
      itemPrice: 0,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: 0,
      taxRateId: defaultTax?.taxRateId || 0,
      taxRate: defaultTax?.taxRate || 0,
      taxPercent: defaultTax?.taxRate || 0,
      taxAmount: 0,
      lineAmount: 0,
      companyId: companyId,
      branchId: branchId || 0,
    }))
  }

  const [creditNote, setCreditNote] = useState<CreditNote>({
    creditNoteId: 0,
    creditNoteDate: new Date().toISOString().split('T')[0],
    creditNoteNumber: '',
    customerId: 0,
    supplyDate: '',
    refInvNumber: '',
    refInvId: 0,
    currency: 0,
    conversionRate: 1,
    taxRateId: 0,
    subTotal: 0,
    totalTax: 0,
    totalDiscount: 0,
    total: 0,
    note: '',
    companyId: companyId,
    branchId: branchId || user?.selectedBranch?.branchId || 0,
    isFullyRefund: false,
    remainingAmount: 0,
    creditNoteLines: getDefaultRows([]),
  })

  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState<number>(0)
  const [items, setItems] = useState<Item[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [invoiceLines, setInvoiceLines] = useState<any[]>([])
  const [fullyRefundedInvoices, setFullyRefundedInvoices] = useState<Set<number>>(new Set())
  const [partiallyRefundedInvoices, setPartiallyRefundedInvoices] = useState<Set<number>>(new Set())

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (!branchId) {
      console.warn('BranchId is not available, cannot load data')
      // Call without await since useEffect callback is not async
      showValidationError('Please select a branch first')
      return
    }

    console.log('Loading lookups for branchId:', branchId)
    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, itemsData, taxRatesData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          financeService.getItemList(branchId),
          financeService.getTaxRates(branchId),
        ])

        console.log('Loaded data:', {
          customers: customersData?.length || 0,
          items: itemsData?.length || 0,
          taxRates: taxRatesData?.length || 0,
        })

        setCustomers(customersData || [])
        setItems(itemsData || [])
        setTaxRates(taxRatesData || [])
        // Don't load invoices initially - will load when customer is selected
        setInvoices([])

        // Load existing credit notes to determine which invoices are fully/partially refunded
        if (!isEditMode) {
          try {
            const creditNotesResponse = await creditNotesService.getAllCreditNotes(branchId, 1, 1000)
            const creditNotes = creditNotesResponse.data || []
            
            const fullyRefunded = new Set<number>()
            const partiallyRefunded = new Set<number>()
            
            // Track invoices that have credit notes
            const invoiceCreditNoteMap = new Map<number, { isFullyRefund: boolean; remainingAmount: number }>()
            
            creditNotes.forEach((cn: any) => {
              const invId = cn.refInvId || cn.invoiceId
              if (invId) {
                const existing = invoiceCreditNoteMap.get(invId) || { isFullyRefund: false, remainingAmount: 0 }
                
                if (cn.isFullyRefund === true || cn.isFullyRefund === 'true') {
                  fullyRefunded.add(invId)
                  existing.isFullyRefund = true
                } else {
                  partiallyRefunded.add(invId)
                  existing.isFullyRefund = false
                }
                
                // Track remaining amount for the invoice
                const originalInv = invoices.find((inv: any) => (inv.invoiceId || inv.id) === invId)
                if (originalInv) {
                  existing.remainingAmount = originalInv.remainingAmount || 0
                }
                
                invoiceCreditNoteMap.set(invId, existing)
              }
            })
            
            console.log('Refunded invoices:', {
              fullyRefunded: Array.from(fullyRefunded),
              partiallyRefunded: Array.from(partiallyRefunded),
              invoiceCreditNoteMap: Array.from(invoiceCreditNoteMap.entries()),
            })
            
            setFullyRefundedInvoices(fullyRefunded)
            setPartiallyRefundedInvoices(partiallyRefunded)
          } catch (error) {
            console.error('Failed to load credit notes for filtering', error)
          }
        }
      } catch (error: any) {
        console.error('Failed to load credit note lookups', error)
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        })
        await showError('Error', error.response?.data?.message || error.message || t('creditNotes.failedToLoadCreditNotes'))
        
        // Set empty arrays to prevent undefined errors
        setCustomers([])
        setItems([])
        setTaxRates([])
        setInvoices([])
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId, isEditMode])

  // Set default VAT (15%) for rows that don't have taxRateId when taxRates are loaded
  useEffect(() => {
    if (taxRates.length === 0) return
    
    const defaultTax = getDefaultTaxRate(taxRates)
    if (!defaultTax) {
      console.warn('Default tax rate (15% or ID 1) not found in taxRates:', taxRates)
      return
    }

    console.log('Setting default VAT:', defaultTax)

    setCreditNote(prev => {
      const hasRowsWithoutVAT = prev.creditNoteLines?.some(line => !line.taxRateId || line.taxRateId === 0)
      if (!hasRowsWithoutVAT) return prev

      const updatedLines = prev.creditNoteLines?.map(line => {
        // Only set default VAT if taxRateId is 0 or not set
        if (!line.taxRateId || line.taxRateId === 0) {
          console.log('Setting default VAT for line:', line, 'to taxRateId:', defaultTax.taxRateId)
          return {
            ...line,
            taxRateId: defaultTax.taxRateId,
            taxRate: defaultTax.taxRate,
            taxPercent: defaultTax.taxRate,
          }
        }
        return line
      }) || []

      return { ...prev, creditNoteLines: updatedLines }
    })
  }, [taxRates])

  // Load credit note data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return

    const loadCreditNote = async () => {
      try {
        setLoading(true)
        const creditNoteId = Number(id)
        
        console.log('Loading credit note data for edit mode:', { branchId, creditNoteId })
        
        // First, set data from list if available (for faster UI update)
        if (creditNoteFromList) {
          const customerId = creditNoteFromList.customerId || 0
          const customer = customers.find((c) => c.customerId === customerId)
          if (customer) {
            setSelectedCustomerId(customerId)
            // Load invoices for this customer
            if (customerId > 0 && branchId) {
              try {
                const invoicesData = await creditNotesService.getAllInvoices(branchId, customerId)
                setInvoices(invoicesData || [])
              } catch (error) {
                console.error('Failed to load invoices for customer in edit mode', error)
              }
            }
          }
          
          setCreditNote((prev) => ({
            ...prev,
            creditNoteId: creditNoteFromList.creditNoteId || creditNoteFromList.id || creditNoteId,
            creditNoteNumber: creditNoteFromList.creditNoteNumber || creditNoteFromList.creditNoteNo || '',
            creditNoteDate: creditNoteFromList.creditNoteDate || new Date().toISOString().split('T')[0],
            customerId: customerId,
            supplyDate: creditNoteFromList.supplyDate || '',
            refInvNumber: creditNoteFromList.refInvNumber || '',
            refInvId: creditNoteFromList.refInvId || 0,
            remainingAmount: creditNoteFromList.remainingAmount || 0,
            isFullyRefund: creditNoteFromList.isFullyRefund === true || creditNoteFromList.isFullyRefund === 'true' || creditNoteFromList.isFullyRefund === 'True',
            subTotal: creditNoteFromList.subTotal || 0,
            totalTax: creditNoteFromList.totalTax || 0,
            totalDiscount: creditNoteFromList.totalDiscount || 0,
            total: creditNoteFromList.total || 0,
            note: creditNoteFromList.note || '',
            branchId: branchId,
          }))
        }
        
        // Now load lines from API (this endpoint returns only lines)
        try {
          const creditNoteData = await creditNotesService.getCreditNoteById(branchId, creditNoteId)
          
          console.log('Loaded credit note lines from API:', creditNoteData.creditNoteLines?.length || 0)
          
          // Update with lines from API - filter out empty lines (only show lines with data)
          if (creditNoteData.creditNoteLines && creditNoteData.creditNoteLines.length > 0) {
            // Filter out empty lines (lines without itemId or with itemId = 0)
            const validLines = creditNoteData.creditNoteLines.filter(
              (line: CreditNoteLine) => line.itemId && line.itemId > 0
            )
            
            setCreditNote((prev) => ({
              ...prev,
              creditNoteLines: validLines.length > 0 ? validLines : [],
              // Update totals if available from API
              subTotal: creditNoteData.subTotal ?? prev.subTotal,
              totalTax: creditNoteData.totalTax ?? prev.totalTax,
              totalDiscount: creditNoteData.totalDiscount ?? prev.totalDiscount,
              total: creditNoteData.total ?? prev.total,
            }))
            
            console.log('Credit note lines set in state:', validLines.length, 'valid lines out of', creditNoteData.creditNoteLines.length)
          } else {
            // If no lines, use empty array (not default rows) in edit mode
            setCreditNote((prev) => ({
              ...prev,
              creditNoteLines: [],
            }))
          }
          
          // If invoice is referenced, load invoice lines for reference
          const refInvId = creditNoteFromList?.refInvId || creditNoteData.refInvId
          if (refInvId) {
            try {
              const invLinesResponse = await creditNotesService.getInvLines(refInvId, branchId)
              setInvoiceLines(invLinesResponse.invoiceLines || [])
            } catch (error) {
              console.warn('Failed to load invoice lines for reference', error)
            }
          }
        } catch (linesError: any) {
          console.error('Failed to load credit note lines', linesError)
          // Don't show error for lines if we have list data
          if (!creditNoteFromList) {
            await showError('Error', linesError.response?.data?.message || linesError.message || t('creditNotes.failedToLoadCreditNote'))
          }
          // Set empty array in edit mode if lines failed to load (don't show default rows)
          setCreditNote((prev) => ({
            ...prev,
            creditNoteLines: [],
          }))
        }
      } catch (error: any) {
        console.error('Failed to load credit note data', error)
        await showError('Error', error.response?.data?.message || error.message || t('creditNotes.failedToLoadCreditNote'))
      } finally {
        setLoading(false)
      }
    }

    // Wait for customers to load before loading credit note
    if (customers.length > 0 || !isEditMode) {
      loadCreditNote()
    }
  }, [isEditMode, branchId, id, customers, creditNoteFromList])

  // Auto-fill lines ONLY when Full Refund is selected and invoice lines are loaded
  useEffect(() => {
    if (
      creditNote.isFullyRefund && 
      invoiceLines.length > 0 && 
      (creditNote.refInvId ?? 0) > 0 && 
      branchId && 
      items.length > 0 && 
      taxRates.length > 0
    ) {
      // Only auto-fill if lines are empty or all lines have itemId = 0
      const hasEmptyLines = !creditNote.creditNoteLines || 
        creditNote.creditNoteLines.length === 0 || 
        creditNote.creditNoteLines.every(line => !line.itemId || line.itemId === 0)
      
      if (hasEmptyLines) {
        const newLines = autoFillLinesFromInvoice(invoiceLines)
        if (newLines.length > 0) {
          setCreditNote((prev) => ({
            ...prev,
            creditNoteLines: newLines,
          }))
        }
      }
    }
  }, [creditNote.isFullyRefund, invoiceLines, creditNote.refInvId, branchId, items, taxRates])

  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return ''
    try {
      if (dateString.includes('T')) {
        return new Date(dateString).toISOString().split('T')[0]
      }
      return dateString.split('T')[0]
    } catch {
      return dateString
    }
  }

  // handleCustomerChange removed - not currently used

  // Helper function to auto-fill credit note lines from invoice lines (Full Refund - all fields)
  const autoFillLinesFromInvoice = (invLines: any[]): CreditNoteLine[] => {
    return invLines.map((invLine: any) => {
      const item = items.find((i) => i.itemId === invLine.itemId)
      const taxRate = taxRates.find((t) => t.taxRateId === invLine.taxRateId)
      
      const qty = invLine.qty || 0
      const unitPrice = invLine.unitPrice || 0
      const taxPercent = taxRate?.taxPercent || taxRate?.taxRate || invLine.taxRate || invLine.taxPercent || 0
      
      // Preserve discount from invoice lines (for Full Refund mode)
      const discountPercent = invLine.discountPercent || 0
      const discountAmount = invLine.discountAmount || 0
      
      // Use invoice values directly when available (for Full Refund)
      const itemPrice = invLine.itemPrice || (qty * unitPrice)
      const taxableAmount = invLine.taxableAmount || (itemPrice - discountAmount)
      const taxAmount = invLine.taxAmount || (taxableAmount * (taxPercent / 100))
      const lineAmount = invLine.lineAmount || (taxableAmount + taxAmount)
      
      return {
        lineId: 0,
        creditNoteId: 0,
        itemId: invLine.itemId || 0,
        description: invLine.description || item?.itemNameEn || item?.itemNameAr || '',
        qty: qty,
        unitPrice: unitPrice,
        itemPrice: itemPrice,
        discountPercent: discountPercent, // Preserve from invoice
        discountAmount: discountAmount, // Preserve from invoice
        taxableAmount: taxableAmount,
        taxRateId: invLine.taxRateId || taxRate?.taxRateId || 0,
        taxRate: taxPercent,
        taxAmount: taxAmount,
        lineAmount: lineAmount,
        companyId: companyId,
        branchId: branchId || 0,
      }
    })
  }

  // Helper function to auto-fill credit note lines from invoice lines (Partial Refund - only items, description, quantity)
  const autoFillLinesFromInvoicePartial = (invLines: any[]): CreditNoteLine[] => {
    const defaultTax = getDefaultTaxRate(taxRates)
    return invLines.map((invLine: any) => {
      const item = items.find((i) => i.itemId === invLine.itemId)
      const taxRate = taxRates.find((t) => t.taxRateId === invLine.taxRateId)
      
      const qty = invLine.qty || 0
      const taxPercent = taxRate?.taxPercent || taxRate?.taxRate || invLine.taxRate || invLine.taxPercent || defaultTax?.taxRate || 0
      const taxRateId = invLine.taxRateId || taxRate?.taxRateId || defaultTax?.taxRateId || 0
      
      return {
        lineId: 0,
        creditNoteId: 0,
        itemId: invLine.itemId || 0,
        description: invLine.description || item?.itemNameEn || item?.itemNameAr || '',
        qty: qty,
        unitPrice: 0, // Don't auto-fill price for partial refund
        itemPrice: 0,
        discountPercent: 0,
        discountAmount: 0,
        taxableAmount: 0,
        taxRateId: taxRateId,
        taxRate: taxPercent,
        taxPercent: taxPercent,
        taxAmount: 0,
        lineAmount: 0,
        companyId: companyId,
        branchId: branchId || 0,
      }
    })
  }

  const calculateLineTotals = (line: CreditNoteLine): CreditNoteLine => {
    const qty = line.qty || 0
    const unitPrice = line.unitPrice || 0
    const taxPercent = line.taxPercent || line.taxRate || 0

    // Use discount if it exists (preserved from invoice in Full Refund mode)
    const discountPercent = line.discountPercent || 0
    const itemPrice = qty * unitPrice
    const discountAmount = itemPrice * (discountPercent / 100)
    const taxableAmount = itemPrice - discountAmount
    const taxAmount = taxableAmount * (taxPercent / 100)
    const lineAmount = taxableAmount + taxAmount

    return {
      ...line,
      itemPrice,
      discountPercent,
      discountAmount,
      taxableAmount,
      taxAmount,
      lineAmount,
    }
  }

  const summaryTotals = useMemo(() => {
    const validLines = creditNote.creditNoteLines?.filter((_, index) => !deletedRowIndices.has(index)) || []
    const subTotal = validLines.reduce((sum, line) => sum + (line.itemPrice || 0), 0)
    const totalDiscount = validLines.reduce((sum, line) => sum + (line.discountAmount || 0), 0)
    const taxableAmount = validLines.reduce((sum, line) => sum + (line.taxableAmount || 0), 0)
    const totalTax = validLines.reduce((sum, line) => sum + (line.taxAmount || 0), 0)
    const total = validLines.reduce((sum, line) => sum + (line.lineAmount || 0), 0)

    return {
      subTotal,
      totalDiscount,
      taxableAmount,
      totalTax,
      total,
    }
  }, [creditNote.creditNoteLines, deletedRowIndices])

  useEffect(() => {
    setCreditNote((prev) => ({
      ...prev,
      subTotal: summaryTotals.subTotal,
      totalDiscount: summaryTotals.totalDiscount,
      totalTax: summaryTotals.totalTax,
      total: summaryTotals.total,
    }))
  }, [summaryTotals])

  const isFormValid = useMemo(() => {
    if (!creditNote.customerId) return false
    if (!creditNote.creditNoteLines || creditNote.creditNoteLines.length === 0) return false

    const validLines = creditNote.creditNoteLines.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      if (isDeleted) return false
      if (!line.itemId || line.itemId === 0) return false
      if (!line.description || line.description.trim() === '') return false
      if (!line.qty || line.qty <= 0) return false
      if (!line.taxRateId || line.taxRateId <= 0) return false
      return true
    })

    return validLines.length > 0
  }, [creditNote.customerId, creditNote.creditNoteLines, deletedRowIndices])

  const handleLineChange = (index: number, field: keyof CreditNoteLine, value: any) => {
    setCreditNote((prev) => {
      const newLines = [...(prev.creditNoteLines || [])]
      const updatedLine = { ...newLines[index], [field]: value }

      if (field === 'itemId' && value) {
        const item = items.find((i) => i.itemId === Number(value))
        if (item) {
          // In partial refund mode, allow price changes, so don't auto-update unitPrice
          // Only update if it's currently 0 or empty
          if (!updatedLine.unitPrice || updatedLine.unitPrice === 0) {
            updatedLine.unitPrice = item.unitPrice || 0
          }
          updatedLine.description = item.itemNameEn || item.itemNameAr || ''
        }
      }

      if (field === 'taxRateId' && value) {
        const taxRate = taxRates.find((t) => t.taxRateId === Number(value))
        if (taxRate) {
          const rateValue = taxRate.taxPercent || taxRate.taxRate || 0
          updatedLine.taxRate = rateValue
          updatedLine.taxPercent = rateValue
        }
      }

      // In partial refund mode, allow unit price changes
      // Only recalculate if it's not a unitPrice change or if it's full refund
      if (field === 'unitPrice' && !prev.isFullyRefund) {
        // Allow manual price changes in partial refund mode
      }

      // In Full Refund mode, preserve invoice values as-is (don't recalculate)
      // Only calculate in Partial Refund mode
      if (prev.isFullyRefund && (prev.refInvId ?? 0) > 0) {
        // Keep invoice values as-is, don't recalculate
        newLines[index] = updatedLine
      } else {
        // Calculate totals for Partial Refund mode
        newLines[index] = calculateLineTotals(updatedLine)
      }
      
      // Validate partial refund total doesn't exceed remaining amount
      if (!prev.isFullyRefund && (prev.remainingAmount ?? 0) > 0) {
        const totalLinesAmount = newLines
          .filter((_, idx) => !deletedRowIndices.has(idx))
          .reduce((sum, line) => sum + (line.lineAmount || 0), 0)
        
        const remainingAmount = prev.remainingAmount ?? 0
        if (totalLinesAmount > remainingAmount) {
          // Call without await since setCreditNote callback is not async
          showValidationError(t('creditNotes.amountCannotExceed', { total: totalLinesAmount.toFixed(2), remaining: remainingAmount.toFixed(2) }))
        }
      }
      
      return { ...prev, creditNoteLines: newLines }
    })
  }

  const handleAddRows = (count: number) => {
    setCreditNote((prev) => {
      const defaultTax = getDefaultTaxRate(taxRates)
      if (defaultTax) {
        console.log('Adding rows with default VAT:', defaultTax)
      } else {
        console.warn('No default tax rate found when adding rows. TaxRates:', taxRates)
      }
      const newLines = Array.from({ length: count }, (): CreditNoteLine => ({
        lineId: 0,
        creditNoteId: prev.creditNoteId || 0,
        itemId: 0,
        description: '',
        qty: 0,
        unitPrice: 0,
        itemPrice: 0,
        discountPercent: 0,
        discountAmount: 0,
        taxableAmount: 0,
        taxRateId: defaultTax?.taxRateId || 0,
        taxRate: defaultTax?.taxRate || 0,
        taxPercent: defaultTax?.taxRate || 0,
        taxAmount: 0,
        lineAmount: 0,
        companyId: companyId,
        branchId: branchId || 0,
      }))
      return { ...prev, creditNoteLines: [...(prev.creditNoteLines || []), ...newLines] }
    })
  }

  const handleDeleteRow = (index: number) => {
    const line = creditNote.creditNoteLines?.[index]
    if (line?.lineId && line.lineId > 0) {
      setDeletedRowIndices((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        } else {
          newSet.add(index)
        }
        return newSet
      })
    } else {
      setCreditNote((prev) => {
        const newLines = [...(prev.creditNoteLines || [])]
        newLines.splice(index, 1)
        return { ...prev, creditNoteLines: newLines }
      })
    }
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      // Try to get IP from a service
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('creditNotes.pleaseSelectBranchFirst'))
      return
    }

    if (!isFormValid) {
      await showValidationError(t('creditNotes.pleaseFillAllRequiredFields'))
      return
    }

    // Show confirmation dialog if Full Refund is selected
    if (creditNote.isFullyRefund) {
      const confirmed = await showConfirm(
        t('creditNotes.confirmFullRefund') || 'Are you sure you want to full refund?',
        t('creditNotes.fullRefundConfirmationMessage') || 'This will process a full refund for the selected invoice.',
        t('common.ok') || 'OK',
        t('common.cancel') || 'Cancel'
      )
      
      if (!confirmed) {
        return // User cancelled, don't save
      }
    }

    // Validate partial refund - total should not exceed remaining amount
    const remainingAmount = creditNote.remainingAmount ?? 0
    if (!creditNote.isFullyRefund && remainingAmount > 0) {
      const totalLinesAmount = creditNote.creditNoteLines
        ?.filter((_, index) => !deletedRowIndices.has(index))
        .reduce((sum, line) => sum + (line.lineAmount || 0), 0) || 0
      
      if (totalLinesAmount > remainingAmount) {
        await showValidationError(t('creditNotes.amountCannotExceed', { total: totalLinesAmount.toFixed(2), remaining: remainingAmount.toFixed(2) }))
        return
      }
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Credit Note...' : 'Saving Credit Note...',
        'Please wait while we process your request'
      )
      
      // Get user IP address (logged-in user's IP, not server's IP)
      const ipAddress = await getUserIPAddress()
      
      // Filter out empty lines (only save lines with actual data)
      // Deleted lines should be included with isDeleted: 'true'
      // Empty lines without itemId should not be saved
      const crInvoiceLine = creditNote.creditNoteLines
        ?.map((line, originalIndex) => {
          const isDeleted = deletedRowIndices.has(originalIndex)
          // For non-deleted lines: only include if they have actual data (itemId > 0)
          // Empty lines without itemId should not be saved
          if (!isDeleted && (!line.itemId || line.itemId === 0)) {
            return null // Skip empty lines (don't save them)
          }
          
          // In Full Refund mode, use invoice values as-is (don't recalculate)
          // Only calculate in Partial Refund mode
          const finalLine = creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0
            ? line // Use invoice values as-is
            : calculateLineTotals(line) // Calculate for partial refund
          
          return {
            lineId: finalLine.lineId || 0,
            invLineId: 0, // Will be set by backend if needed
            creditNoteId: creditNote.creditNoteId || 0,
            itemId: finalLine.itemId || 0,
            description: finalLine.description || '',
            qty: finalLine.qty || 0,
            unitPrice: finalLine.unitPrice || 0,
            itemPrice: finalLine.itemPrice || 0,
            discountPercent: finalLine.discountPercent || 0,
            discountAmount: finalLine.discountAmount || 0,
            taxRateId: finalLine.taxRateId || 0,
            taxAmount: finalLine.taxAmount || 0,
            lineAmount: finalLine.lineAmount || 0,
            isDeleted: isDeleted ? 'true' : 'false', // Lowercase "true" or "false" (small letters)
          }
        })
        .filter((line): line is NonNullable<typeof line> => line !== null) || []

      // Transform to API structure
      const creditNoteToSave = {
        creditNoteId: creditNote.creditNoteId || 0,
        creditNoteDate: creditNote.creditNoteDate ? new Date(creditNote.creditNoteDate).toISOString() : new Date().toISOString(),
        invoiceId: creditNote.refInvId || 0,
        subTotal: creditNote.subTotal || 0,
        totalTax: creditNote.totalTax || 0,
        totalDiscount: creditNote.totalDiscount || 0,
        total: creditNote.total || 0,
        isFullyRefund: creditNote.isFullyRefund ? 'true' : 'false',
        note: creditNote.note || '',
        ipAddress: ipAddress,
        crInvoiceLine: crInvoiceLine,
      }

      // Log the data structure being sent
      console.log('=== Credit Note Save Data Structure ===')
      console.log('Mode:', isEditMode ? 'UPDATE' : 'CREATE')
      console.log('API Payload:', JSON.stringify(creditNoteToSave, null, 2))
      console.log('======================================')

      if (isEditMode) {
        await creditNotesService.updateCreditNote(creditNoteToSave)
      } else {
        await creditNotesService.insertCreditNote(creditNoteToSave)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('creditNotes.creditNoteUpdatedSuccess') : t('creditNotes.creditNoteCreatedSuccess')
      )
      
      navigate('/credit-notes')
    } catch (error: any) {
      console.error('Failed to save credit note', error)
      console.error('Error response:', error.response?.data)
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const customerOptions: SearchableSelectOption[] = useMemo(() => {
    const options = customers.map((customer) => ({
      value: String(customer.customerId),
      label: customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`,
    }))
    return options
  }, [customers])

  const itemOptions: SearchableSelectOption[] = useMemo(() => {
    const options = items.map((item) => ({
      value: String(item.itemId),
      label: item.itemNameEn || item.itemNameAr || `Item ${item.itemId}`,
    }))
    console.log('Item options:', options.length)
    return options
  }, [items])

  const taxRateOptions: SearchableSelectOption[] = useMemo(() => {
    const options = taxRates.map((tax) => ({
      value: String(tax.taxRateId),
      label: `${tax.taxNameEn || tax.taxNameAr || 'Tax'} (${tax.taxPercent || tax.taxRate || 0}%)`,
    }))
    console.log('Tax rate options:', options.length)
    return options
  }, [taxRates])

  // Load invoices when customer is selected
  useEffect(() => {
    if (!branchId || !selectedCustomerId || selectedCustomerId === 0) {
      setInvoices([])
      return
    }

    const loadInvoices = async () => {
      try {
        console.log('Loading invoices for customer:', selectedCustomerId, 'branchId:', branchId)
        const invoicesData = await creditNotesService.getAllInvoices(branchId, selectedCustomerId)
        console.log('Loaded invoices for customer:', invoicesData.length)
        setInvoices(invoicesData || [])
      } catch (error: any) {
        console.error('Failed to load invoices for customer', error)
        setInvoices([])
      }
    }

    loadInvoices()
  }, [branchId, selectedCustomerId])

  // Filter invoices: exclude fully refunded invoices and invoices with remainingAmount = 0
  const availableInvoices = useMemo(() => {
    console.log('Filtering invoices:', {
      totalInvoices: invoices.length,
      fullyRefundedCount: fullyRefundedInvoices.size,
      isEditMode,
      selectedCustomerId,
    })
    
    if (isEditMode) {
      return invoices // Show all invoices in edit mode
    }
    // Filter out fully refunded invoices and invoices with remainingAmount = 0
    const filtered = invoices.filter((inv) => {
      const invId = inv.invoiceId || inv.id
      const isFullyRefunded = fullyRefundedInvoices.has(invId)
      const remainingAmount = inv.remainingAmount || 0
      
      if (isFullyRefunded) {
        console.log('Filtering out fully refunded invoice:', invId)
        return false
      }
      if (remainingAmount === 0) {
        console.log('Filtering out invoice with zero remaining amount:', invId)
        return false
      }
      return true
    })
    
    console.log('Available invoices after filtering:', filtered.length)
    return filtered
  }, [invoices, fullyRefundedInvoices, isEditMode, selectedCustomerId])

  const invoiceOptions: SearchableSelectOption[] = useMemo(() => {
    const options = [
      { value: '0', label: 'None' },
      ...availableInvoices.map((inv) => ({
        value: String(inv.invoiceId || inv.id || 0),
        label: inv.invoiceNumber || inv.invoiceNo || `Invoice ${inv.invoiceId || inv.id || 0}`,
      })),
    ]
    console.log('Invoice options:', options.length, 'available invoices:', availableInvoices.length)
    return options
  }, [availableInvoices])

  // Dynamic "Is Fully Refund" options based on selected invoice
  const isFullyRefundOptions: SearchableSelectOption[] = useMemo(() => {
    const refInvId = creditNote.refInvId ?? 0
    if (!refInvId) {
      return [
        { value: 'false', label: 'Partial Refund' },
        { value: 'true', label: 'Full Refund' },
      ]
    }
    
    // If invoice has been partially refunded before (has existing credit note), only show Partial Refund
    if (partiallyRefundedInvoices.has(refInvId)) {
      return [
        { value: 'false', label: 'Partial Refund' },
      ]
    }
    
    // Check if invoice has existing credit notes by checking remaining amount
    const selectedInv = availableInvoices.find(inv => (inv.invoiceId || inv.id) === refInvId)
    if (!selectedInv) {
      // Invoice not found in available list (might be fully refunded or zero remaining)
      return [
        { value: 'false', label: 'Partial Refund' },
      ]
    }
    
    const remainingAmount = selectedInv.remainingAmount || 0
    const originalTotal = selectedInv.total || selectedInv.amount || 0
    
    // If invoice has existing credit notes (remaining < original), only show Partial Refund
    // Full Refund is only available for first-time credit notes
    if (remainingAmount < originalTotal && originalTotal > 0) {
      return [
        { value: 'false', label: 'Partial Refund' },
      ]
    }
    
    // First time credit note for this invoice - show both options
    return [
      { value: 'false', label: 'Partial Refund' },
      { value: 'true', label: 'Full Refund' },
    ]
  }, [creditNote.refInvId, partiallyRefundedInvoices, availableInvoices])

  // Remove unused customerOptions - customers are handled via SearchableSelect directly

  if (loading && !creditNote.creditNoteLines?.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={isEditMode ? t('creditNotes.editCreditNote') : t('creditNotes.newCreditNote')}
        description={isEditMode ? t('creditNotes.updateCreditNoteDetails') : t('creditNotes.createNewCreditNote')}
      />

      {/* Client Details card - completely removed as customer name is shown in Credit Note section */}

      <Card>
        <CardHeader>
          <CardTitle>{t('creditNotes.creditNote')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>{t('creditNotes.creditNoteDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(creditNote.creditNoteDate)}
                onChange={(e) => setCreditNote((prev) => ({ ...prev, creditNoteDate: e.target.value }))}
              />
            </div>
            {isEditMode && (
              <div className="space-y-2">
                <Label>{t('creditNotes.creditNoteNumberFull')}</Label>
                <Input value={creditNote.creditNoteNumber || ''} readOnly className="bg-muted" placeholder={t('creditNotes.autoGenerated')} />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t('creditNotes.customer') || t('common.customer')}</Label>
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomerId ? String(selectedCustomerId) : '0'}
                onValueChange={(value) => {
                  const customerId = Number(value) || 0
                  setSelectedCustomerId(customerId)
                  // Reset invoice when customer changes
                  setCreditNote((prev) => ({
                    ...prev,
                    customerId: customerId,
                    refInvId: 0,
                    refInvNumber: '',
                    remainingAmount: 0,
                    isFullyRefund: false,
                    supplyDate: '',
                    creditNoteLines: getDefaultRows(taxRates),
                  }))
                  setInvoiceLines([])
                }}
                placeholder={t('creditNotes.selectCustomer')}
                searchPlaceholder={t('creditNotes.searchCustomers')}
                emptyMessage={t('creditNotes.noCustomersFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>Reference Invoice #</Label>
              <SearchableSelect
                options={invoiceOptions}
                value={creditNote.refInvId ? String(creditNote.refInvId) : '0'}
                disabled={!selectedCustomerId || selectedCustomerId === 0}
                onValueChange={async (value) => {
                  const invId = Number(value) || 0
                  const selectedInv = availableInvoices.find(inv => (inv.invoiceId || inv.id) === invId)
                  
                  if (invId === 0) {
                    // Reset when "None" is selected
                    setCreditNote((prev) => ({
                      ...prev,
                      refInvId: 0,
                      refInvNumber: '',
                      remainingAmount: 0,
                      isFullyRefund: false,
                      creditNoteLines: getDefaultRows(taxRates),
                    }))
                    setInvoiceLines([])
                    return
                  }
                  
                  // Load invoice lines using new API structure
                  let loadedLines: any[] = []
                  let invoiceData: any = null
                  if (branchId && invId) {
                    try {
                      console.log('Loading invoice lines for invoice ID:', invId, 'branchId:', branchId)
                      const response = await creditNotesService.getInvLines(invId, branchId)
                      console.log('Loaded invoice data:', response)
                      invoiceData = response.invoice || {}
                      loadedLines = response.invoiceLines || []
                      setInvoiceLines(loadedLines)
                      
                      if (loadedLines.length === 0) {
                        await showError('No Data', 'No invoice lines found for this invoice')
                      }
                    } catch (error: any) {
                      console.error('Failed to load invoice lines', error)
                      await showError('Error', error.response?.data?.message || 'Failed to load invoice lines')
                      setInvoiceLines([])
                    }
                  }
                  
                  // Determine if this should be partial refund (if invoice was partially refunded before)
                  const shouldBePartialRefund = partiallyRefundedInvoices.has(invId)
                  const isFullRefund = shouldBePartialRefund ? false : creditNote.isFullyRefund
                  
                  // Get supply date from invoice or invoice data
                  const invoiceSupplyDate = invoiceData?.supplyDate || selectedInv?.supplyDate || ''
                  
                  setCreditNote((prev) => {
                    const updated = {
                      ...prev,
                      refInvId: invId,
                      refInvNumber: selectedInv?.invoiceNumber || selectedInv?.invoiceNo || '',
                      remainingAmount: selectedInv?.remainingAmount || selectedInv?.total || 0,
                      isFullyRefund: isFullRefund,
                      customerId: selectedInv?.customerId || prev.customerId,
                      // Set supply date from invoice
                      supplyDate: invoiceSupplyDate || prev.supplyDate,
                    }
                    
                    // Don't auto-fill lines when invoice is selected
                    // Lines will be auto-filled when "Full Refund" or "Partial Refund" is selected
                    
                    return updated
                  })
                }}
                placeholder={t('creditNotes.selectInvoice')}
                searchPlaceholder={t('creditNotes.searchInvoices')}
                emptyMessage={t('creditNotes.noInvoicesFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('creditNotes.supplyDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(creditNote.supplyDate)}
                onChange={(e) => setCreditNote((prev) => ({ ...prev, supplyDate: e.target.value }))}
                readOnly={(creditNote.refInvId ?? 0) > 0}
                className={(creditNote.refInvId ?? 0) > 0 ? 'bg-muted' : ''}
                title={(creditNote.refInvId ?? 0) > 0 ? 'Supply date is set from invoice' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('creditNotes.isFullyRefund')}</Label>
              <SearchableSelect
                options={isFullyRefundOptions}
                value={creditNote.isFullyRefund ? 'true' : 'false'}
                onValueChange={async (value) => {
                  const isFullRefund = value === 'true'
                  
                  if (invoiceLines.length > 0 && branchId && items.length > 0 && taxRates.length > 0) {
                    if (isFullRefund) {
                      // Full Refund: auto-fill all fields including prices
                      const newLines = autoFillLinesFromInvoice(invoiceLines)
                      setCreditNote((prev) => ({
                        ...prev,
                        isFullyRefund: true,
                        creditNoteLines: newLines,
                      }))
                    } else {
                      // Partial Refund: auto-fill only items, description, and quantity (not prices)
                      const newLines = autoFillLinesFromInvoicePartial(invoiceLines)
                      setCreditNote((prev) => ({
                        ...prev,
                        isFullyRefund: false,
                        creditNoteLines: newLines,
                      }))
                    }
                  } else if (invoiceLines.length > 0) {
                    // If items/taxRates not loaded yet, wait for useEffect
                    setCreditNote((prev) => ({ ...prev, isFullyRefund: isFullRefund }))
                  } else {
                    // No invoice lines available
                    setCreditNote((prev) => ({ 
                      ...prev, 
                      isFullyRefund: isFullRefund,
                      creditNoteLines: getDefaultRows(taxRates),
                    }))
                  }
                }}
                placeholder={t('common.select')}
                disabled={!(creditNote.refInvId ?? 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('creditNotes.remainingAmount')}</Label>
              <Input
                type="number"
                step="0.01"
                value={creditNote.remainingAmount || ''}
                readOnly
                className="bg-muted"
                placeholder="0.00"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardHeader className="bg-primary/5">
          <div className="flex items-center justify-between">
            <CardTitle>{t('creditNotes.creditNoteLines')}</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleAddRows(1)}>
                <Plus className="w-4 h-4 mr-1" />
                {t('creditNotes.addLine')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAddRows(3)}>
                <Plus className="w-4 h-4 mr-1" />
                {t('invoices.add3Rows')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAddRows(5)}>
                <Plus className="w-4 h-4 mr-1" />
                {t('invoices.add5Rows')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-primary/5">
                  <th className="text-left p-3 font-semibold align-middle">ITEM</th>
                  <th className="text-left p-3 font-semibold align-middle">DESCRIPTION <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">QTY <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">UNIT PRICE</th>
                  <th className="text-left p-3 font-semibold align-middle">ITEM PRICE</th>
                  <th className="text-left p-3 font-semibold align-middle">TAXABLE</th>
                  <th className="text-left p-3 font-semibold align-middle">VAT <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">VAT AMOUNT</th>
                  <th className="text-left p-3 font-semibold align-middle">AMOUNT</th>
                  <th className="text-left p-3 font-semibold align-middle w-20"></th>
                </tr>
              </thead>
              <tbody>
                {creditNote.creditNoteLines?.map((line, index) => {
                  const isDeleted = deletedRowIndices.has(index)
                  return (
                    <tr
                      key={index}
                      className={`border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/50 ${
                        isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={itemOptions}
                            value={line.itemId ? String(line.itemId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'itemId', Number(value))}
                            placeholder="Select item..."
                            searchPlaceholder="Search items..."
                            emptyMessage="No items found"
                            disabled={saving || isDeleted || (creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0)}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            value={line.description || ''}
                            onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                            placeholder="Description"
                            disabled={saving || isDeleted || (creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0)}
                            className="min-w-[200px]"
                            readOnly={creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            value={line.qty || ''}
                            onChange={(e) => handleLineChange(index, 'qty', Number(e.target.value) || 0)}
                            placeholder="0"
                            disabled={saving || isDeleted || (creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0)}
                            className="w-20"
                            readOnly={creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.unitPrice || ''}
                            onChange={(e) => handleLineChange(index, 'unitPrice', Number(e.target.value) || 0)}
                            placeholder="0.00"
                            disabled={saving || isDeleted || (creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0)}
                            className="w-24"
                            title={creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0 ? 'Unit price is locked in Full Refund mode' : ''}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.itemPrice?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.taxableAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={taxRateOptions}
                            value={line.taxRateId ? String(line.taxRateId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'taxRateId', Number(value))}
                            placeholder="Select VAT"
                            searchPlaceholder="Search VAT..."
                            emptyMessage="No VAT rates found"
                            disabled={saving || isDeleted || (creditNote.isFullyRefund && (creditNote.refInvId ?? 0) > 0)}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.taxAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.lineAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRow(index)}
                          disabled={saving}
                          className={
                            isDeleted
                              ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                              : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                          }
                          title={isDeleted ? 'Undo delete' : 'Delete row'}
                        >
                          {isDeleted ? <span className="text-xs font-semibold">Undo</span> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-primary/5 p-4 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">SUB TOTAL</Label>
                <p className="text-lg font-semibold">{summaryTotals.subTotal.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL VAT</Label>
                <p className="text-lg font-semibold">{summaryTotals.totalTax.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL</Label>
                <p className="text-lg font-semibold">{summaryTotals.total.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notes / Remarks</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={creditNote.note || ''}
            onChange={(e) => setCreditNote((prev) => ({ ...prev, note: e.target.value }))}
            placeholder="Enter notes or remarks..."
            className="min-h-[100px]"
          />
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button type="button" variant="outline" onClick={() => navigate('/credit-notes')} className="w-full sm:w-auto">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !branchId || !isFormValid}
          className="w-full sm:w-auto min-w-[150px]"
          title={!isFormValid ? t('creditNotes.pleaseFillAllRequiredFields') : ''}
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('creditNotes.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('creditNotes.updateCreditNote') : t('creditNotes.saveCreditNote')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

